import {
    GridInput,
    GridOutput,
    CoreGridInput,
    CoreReceivingsGridOutput,
    fromCoreReceivingNames,
    Optional,
    ReceivingDetailInterface,
    ReceivingResponse,
    ReceivedReceiving,
    Receiving,
} from 'skladon-types';

import { ReceivingsMapper } from '../ReceivingsMapper';
import { CoreGridMapper } from '../CoreGridMapper';
import { toNumber } from '../numbers';
import { asInt, asNumber } from './coerceNumbers';

/** Detail row or grid row from Core (the grid omits nested relations). */
type ReceivingFromCoreInput =
    | Optional<ReceivingResponse, 'PRODUCTS' | 'PROVIDER'>
    | Omit<ReceivingDetailInterface, 'PRODUCTS' | 'PROVIDER'>;

interface ReceivingV21Output {
    driverCompany: string | null;
    driverCompanyOtherName: string | null;
    [key: string]: unknown;
}

export class ReceivingsMapperV21 {
    public static fromCore(receiving: ReceivingFromCoreInput): ReceivingV21Output {
        const base = ReceivingsMapper.fromCore(receiving);
        return {
            ...base,
            products: base.products?.map((product) => ({
                ...product,
                quantity: asInt(product.quantity),
                received: asInt(product.received),
                productPrice: toNumber(product.productPrice),
            })),
            unitsAmount: asInt(base.unitsAmount),
            announcedPalletsCount: asInt(base.announcedPalletsCount),
            deliveredPalletsCount: asInt(base.deliveredPalletsCount),
            deliveredCartonsCount: asInt(base.deliveredCartonsCount),
            returnedEurPalletsCount: asInt(base.returnedEurPalletsCount),
            deliveredStandardPalletsCount: asInt(base.deliveredStandardPalletsCount),
            returnedStandardPalletsCount: asInt(base.returnedStandardPalletsCount),
            deliveredOtherPalletsCount: asInt(base.deliveredOtherPalletsCount),
            deliveredBrokenPalletsCount: asInt(base.deliveredBrokenPalletsCount),
            driverCompany: receiving.DRIVER_COMPANY || null,
            driverCompanyOtherName: receiving.DRIVER_COMPANY_OTHER_NAME || null,
        };
    }

    public static toCore(receiving: Partial<Receiving> & { driverCompanyOtherName?: string | null }): ReceivedReceiving {
        const base = ReceivingsMapper.toCore(receiving as Receiving);
        return {
            ...base,
            DRIVER_COMPANY: receiving.driverCompany || null,
            DRIVER_COMPANY_OTHER_NAME: receiving.driverCompany === 'other'
                ? (receiving.driverCompanyOtherName || null)
                : null,
        } as ReceivedReceiving;
    }

    public static toCorePatch(
        receiving: Partial<Receiving> & { driverCompanyOtherName?: string | null }
    ): Partial<ReceivedReceiving> {
        const core = ReceivingsMapper.toCorePatch(receiving);
        if (receiving.driverCompany !== undefined) {
            core.DRIVER_COMPANY = receiving.driverCompany || null;
            core.DRIVER_COMPANY_OTHER_NAME =
                receiving.driverCompany === 'other'
                    ? (receiving.driverCompanyOtherName || null)
                    : null;
        }
        return core;
    }

    public static fromCoreGrid(grid: CoreReceivingsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreReceiving) => {
                    const row = ReceivingsMapperV21.fromCore(coreReceiving);
                    const totals = coreReceiving as {
                        QUANTITY?: number | string | null;
                        RECEIVED?: number | string | null;
                        DIFF_QUANTITY?: number | string | null;
                    };
                    const withTotals = { ...row };
                    if ('QUANTITY' in coreReceiving) {
                        withTotals.quantity = asNumber(totals.QUANTITY);
                    }
                    if ('RECEIVED' in coreReceiving) {
                        withTotals.received = asNumber(totals.RECEIVED);
                    }
                    if ('DIFF_QUANTITY' in coreReceiving) {
                        withTotals.diffQuantity = asNumber(totals.DIFF_QUANTITY);
                    }
                    return withTotals;
                })
                .map((receiving) =>
                    CoreGridMapper.omitColumns(receiving, fromCoreReceivingNames, grid.request.columns)
                ),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreReceivingNames, 1000, ['duty']),
            totalCount: grid.totalCount,
        };
    }

    public static toCoreGrid = ReceivingsMapper.toCoreGrid;
}
